import { SearchableMultiSelect } from '@/modules/common/components/SearchableMultiSelect/SearchableMultiSelect';
import { TableFilter } from '@/modules/common/components/TableFilter';
import { useEducationProviders } from '@/modules/common/hooks/data-hooks/useEducationProviders';
import { useIndividualEducationProviders } from '@/modules/common/hooks/data-hooks/useIndividualEducationProviders';
import { formatSlug } from '@/modules/common/lib/formatters';
import { formatConstant } from '@/modules/common/lib/string';
import { IEducationProvider } from '@/types/education-provider';
import { PaymentStatus } from '@/types/payment';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Button, Checkbox, ListItemText, MenuItem } from '@mui/material';
import { ColumnFiltersState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

type FilterToolbarProps = {
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (filters: ColumnFiltersState) => void;
  allowedStatuses: PaymentStatus[];
  onExport?: () => void;
  isExporting?: boolean;
  showClearFilters?: boolean;
  onClearFilters?: () => void;
  hideEducationProviderFilter?: boolean;
};

export function FilterToolbar({
  columnFilters,
  onColumnFiltersChange,
  allowedStatuses,
  onExport,
  isExporting = false,
  showClearFilters = false,
  onClearFilters,
  hideEducationProviderFilter,
}: FilterToolbarProps) {
  const [educationProviderSearch, setEducationProviderSearch] = useState('');

  const statusFilter = columnFilters.find(filter => filter.id === 'status')
    ?.value as PaymentStatus[] | undefined;

  const educationProviderFilter = columnFilters.find(
    filter => filter.id === 'educationProvider',
  )?.value as string[] | undefined;

  const { data: { data: educationProviders = [] } = {} } =
    useEducationProviders({
      filters: {
        ...(educationProviderSearch && { name: educationProviderSearch }),
      },
    });

  const {
    data: educationProvidersData,
    isLoading: isLoadingEducationProviders,
  } = useIndividualEducationProviders(educationProviderFilter || [], {
    enabled: !!educationProviderFilter?.length,
  });

  const availableEducationProviders = useMemo(() => {
    return [...(educationProviders ?? []), ...(educationProvidersData ?? [])]
      .filter(
        (ep, index, self) =>
          ep && ep?._id && index === self.findIndex(t => t?._id === ep?._id),
      )
      .filter(ep => {
        if (ep && educationProviderFilter?.includes(ep._id)) {
          return true;
        }
        return educationProviders?.some(t => t._id === ep?._id);
      })
      .filter(ep => ep) as IEducationProvider[];
  }, [educationProviders, educationProvidersData, educationProviderFilter]);

  const statusFilterOptions = allowedStatuses.map(status => ({
    label: formatSlug(status.toLowerCase()),
    value: status,
  }));

  const handleStatusChange = (value: PaymentStatus[]) => {
    onColumnFiltersChange([
      ...columnFilters.filter(({ id }) => id !== 'status'),
      {
        id: 'status',
        value,
      },
    ]);
  };

  const handleEducationProviderChange = (value: string[]) => {
    onColumnFiltersChange([
      ...columnFilters.filter(({ id }) => id !== 'educationProvider'),
      ...(value.length > 0
        ? [
            {
              id: 'educationProvider',
              value,
            },
          ]
        : []),
    ]);
  };

  return (
    <nav className="flex gap-12 py-6 items-center">
      <TableFilter<PaymentStatus[] | undefined>
        label="Status"
        id="status-select"
        value={statusFilter ?? []}
        multiple
        onChange={e => handleStatusChange(e.target.value as PaymentStatus[])}
        displayEmpty
        renderValue={selected => {
          if (!selected?.length) {
            return 'All';
          }
          return (selected || []).map(formatConstant).join(', ');
        }}
      >
        {statusFilterOptions.map(option => (
          <MenuItem key={option.value} value={option.value}>
            <div className="flex gap-2 items-center capitalize">
              <Checkbox
                checked={statusFilter?.includes(option.value) ?? false}
                sx={({ palette }) => ({
                  padding: 0,
                  '&.Mui-checked': {
                    color: palette.primary,
                  },
                })}
              />
              <ListItemText>{formatConstant(option.label)}</ListItemText>
            </div>
          </MenuItem>
        ))}
      </TableFilter>

      {!hideEducationProviderFilter && (
        <SearchableMultiSelect
          id="education-provider-filter"
          label="Education Provider"
          value={educationProviderFilter || []}
          isLoading={isLoadingEducationProviders}
          onChange={handleEducationProviderChange}
          options={availableEducationProviders.map(ep => ({
            id: ep._id!,
            label: ep.name,
          }))}
          onSearch={setEducationProviderSearch}
          searchPlaceholder="Search education providers..."
        />
      )}

      {showClearFilters && onClearFilters && (
        <div className="flex items-center">
          <Button
            variant="text"
            className="text-xs font-medium text-mydra-purple"
            onClick={onClearFilters}
          >
            Clear filters
          </Button>
        </div>
      )}

      {onExport && (
        <Button
          type="button"
          size="small"
          variant="text"
          onClick={onExport}
          sx={{
            color: 'text.secondary',
          }}
          className="ml-auto font-semibold"
          disabled={isExporting}
          startIcon={<FileDownloadOutlined fontSize="small" />}
        >
          Export data
        </Button>
      )}
    </nav>
  );
}
